import type { EditorHost } from '@blocksuite/affine/block-std';
import { ShadowlessElement } from '@blocksuite/affine/block-std';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import type { SpecBuilder } from '@blocksuite/affine/shared/utils';
import { html } from 'lit';
import { property } from 'lit/decorators.js';

import { createTextRenderer } from '../../components/text-renderer';

/**
 * Applies life sciences formatting to AI responses:
 * - Bold for hypotheses, findings, conclusions
 * - Italic for uncertainties (may, might, possibly, uncertain)
 */
function formatLifeSciencesContent(text: string): string {
  if (!text) return text;

  let formatted = text;

  // Bold key findings patterns: "Hypothesis:", "Finding:", "Conclusion:", "Result:", "Key insight:"
  // Only format if not already in markdown formatting
  formatted = formatted.replace(
    /(?<![\*_`])((?:Hypothesis|Finding|Conclusion|Result|Key insight|Main finding|Principal finding):\s*)([^.\n]+[.\n])/gi,
    '**$1$2**'
  );

  // Italicize uncertainty phrases at the start of sentences or after punctuation
  // Patterns: "may be", "might", "possibly", "potentially", "uncertain", "appears to"
  const uncertaintyPattern =
    /(?<=^|[.!?]\s)([^.!?\n]*?\b(?:may|might|possibly|potentially|uncertain|appears to|seems to|could be|it is possible|suggesting|indicating)\b[^.!?\n]*?[.!?])/gi;

  formatted = formatted.replace(uncertaintyPattern, match => {
    // Don't double-wrap if already formatted
    if (match.startsWith('*') && match.endsWith('*')) return match;
    return `*${match.trim()}*`;
  });

  return formatted;
}

export class ChatContentRichText extends WithDisposable(ShadowlessElement) {
  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor text!: string;

  @property({ attribute: false })
  accessor state: 'finished' | 'generating' = 'finished';

  @property({ attribute: false })
  accessor previewSpecBuilder!: SpecBuilder;

  protected override render() {
    const { text, host } = this;
    // Apply life sciences formatting for hypotheses and uncertainties
    const formattedText = formatLifeSciencesContent(text);
    return html`${createTextRenderer(host, {
      customHeading: true,
      extensions: this.previewSpecBuilder.value,
    })(formattedText, this.state)}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-content-rich-text': ChatContentRichText;
  }
}
